import React from 'react';
import { cn } from '@mellify/ui';
import { AppLayout } from './AppLayout';
import { TopNavbar } from '../components/navigation/TopNavbar';
import { Breadcrumbs } from '../components/navigation/Breadcrumbs';
import type { BreadcrumbItem } from '../components/navigation/Breadcrumbs';

export interface DashboardLayoutProps {
  /** Page content */
  children: React.ReactNode;
  /** Currently active route path for sidebar highlighting */
  activePath?: string;
  /** Breadcrumb items for the current page */
  breadcrumbs?: BreadcrumbItem[];
  /** Page title displayed in the content area */
  pageTitle?: string;
  /** Optional page description */
  pageDescription?: string;
  /** Optional actions to display in the page header (right side) */
  pageActions?: React.ReactNode;
  /** Additional className for the scrollable content area */
  className?: string;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
  activePath = '/dashboard',
  breadcrumbs = [],
  pageTitle,
  pageDescription,
  pageActions,
  className,
}) => {
  return (
    <AppLayout activePath={activePath}>
      {/* Top Navbar */}
      <TopNavbar />

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        <div className="mx-auto max-w-content-xl px-4 sm:px-6 lg:px-8 py-6">
          {/* Breadcrumbs */}
          {breadcrumbs.length > 0 && (
            <Breadcrumbs items={breadcrumbs} className="mb-4" />
          )}

          {/* Page header */}
          {(pageTitle || pageActions) && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                {pageTitle && (
                  <h1 className="text-2xl font-bold text-dark-900 dark:text-dark-100 tracking-tight">
                    {pageTitle}
                  </h1>
                )}
                {pageDescription && (
                  <p className="mt-1 text-sm text-dark-500 dark:text-dark-400">
                    {pageDescription}
                  </p>
                )}
              </div>
              {pageActions && (
                <div className="flex items-center gap-3 shrink-0">
                  {pageActions}
                </div>
              )}
            </div>
          )}

          {/* Main content */}
          <div className={cn(className)}>{children}</div>
        </div>
      </div>
    </AppLayout>
  );
};

DashboardLayout.displayName = 'DashboardLayout';